import { createBrowserClient } from '@/supabase'
import { calculateTotalExperience } from '@/lib/transformExperienceDate'
import type { PaginationParams, PaginationResponse } from '@/types/common'

export const searchMakers = async (
  filters: {
    experience?: [number, number]
    job?: string[]
    specialization?: string[]
  },
  pagination?: PaginationParams
): Promise<PaginationResponse<any>> => {
  const { experience, job, specialization } = filters
  const { page = 1, pageSize = 20 } = pagination || {}
  const supabase = createBrowserClient()

  // Supabase는 0부터 시작하므로 인덱스 계산
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  let query = supabase
    .from('accounts')
    .select(
      `
      *,
      account_work_experiences (*),
      account_educations (*),
      account_license (*)
    `,
      { count: 'exact' }
    )
    .eq('is_active', true) // 활성 프로필만
    .or('is_discoverable.eq.true,is_onboarded.eq.true') // 노출 가능 또는 온보딩 완료(동료 찾기에서 둘 다 노출)
    .eq('availability_status', 'available') // 현재 배정 가능한 프로필만
    .is('deleted_at', null)

  // 필터 적용
  if (job && job.length > 0) {
    // main_job 배열에 선택된 직무가 포함된 경우 필터링
    // Supabase 배열 필터링: cs 연산자 사용
    const jobFilters = job.map(j => `main_job.cs.{${j}}`).join(',')
    query = query.or(jobFilters)
  }

  if (specialization && specialization.length > 0) {
    // expertise 배열에 선택된 전문분야가 포함된 경우 필터링
    const specFilters = specialization.map(s => `expertise.cs.{${s}}`).join(',')
    query = query.or(specFilters)
  }

  // 정렬 및 범위 설정
  const { data, error, count } = await query
    .order('created_at', { ascending: false })
    .range(from, to)

  if (error) {
    console.error('searchMakers 쿼리 에러:', error)
    throw error
  }

  // 경력 필터링 (클라이언트 사이드)
  // 주의: 경력 필터링은 클라이언트 사이드에서 처리하므로, 
  // 실제 반환되는 데이터 수가 pageSize보다 적을 수 있습니다.
  // 이 경우 다음 페이지를 불러와야 할 수도 있습니다.
  let filteredData = data || []
  
  if (experience && experience.length === 2) {
    const [minYears, maxYears] = experience
    filteredData = filteredData.filter((maker: any) => {
      // 경력 데이터가 없는 경우
      if (!maker.account_work_experiences || maker.account_work_experiences.length === 0) {
        return minYears === 0 // 신입 필터만 통과
      }
      
      // 타입 변환: null → undefined
      const experiences = maker.account_work_experiences.map((exp: any) => ({
        start_date: exp.start_date,
        end_date: exp.end_date ?? undefined
      }))
      
      const totalExp = calculateTotalExperience(experiences)
      const totalYears = totalExp.years + totalExp.months / 12
      
      // 신입 필터 (minYears === 0): 경력이 1년 미만인 경우도 포함
      if (minYears === 0 && totalYears < 1) {
        return true
      }
      
      // 일반 필터: 총 경력이 범위 내인지 확인
      return totalYears >= minYears && (maxYears === null || totalYears <= maxYears)
    })
  }

  // 다음 페이지가 있는지 확인
  // 경력 필터링으로 인해 실제 데이터가 적을 수 있으므로, 
  // 원본 데이터가 pageSize와 같고 count가 더 많으면 다음 페이지가 있다고 판단
  const hasNextPage = data && data.length === pageSize && (count ?? 0) > page * pageSize
  
  return {
    data: filteredData,
    count,
    nextPage: hasNextPage ? page + 1 : undefined,
  }
}
